import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import Button from '../../../components/ui/Button'
import type { SystemParameter } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  onEdit: (row: SystemParameter) => void
  /**
   * Salt okunur kip (126 — Kısıtlı Yetkili asistan): işlemler kolonu hiç çizilmez. Kod/ad/değer zaten
   * tabloda göründüğü için ayrı bir görüntüleme eylemine gerek yoktur.
   */
  readOnly?: boolean
}

/**
 * Sistem parametresi tablosu (kod/ad/değer + yalnız Düzenle; ekle/sil yok).
 *
 * Kolonlara **yüzde genişlik** verilir: tablo `.admin-table-wrap` ile `table-layout: fixed`
 * çalışır, böylece uzun `CODE`/`NAME_*`/`VALUE` değerleri (hepsi VARCHAR2(1000)) yana taşmak
 * yerine hücre içinde alt satıra sarılır ve tablo yatay kaydırma çubuğu olmadan ekrana sığar.
 * Yüzdeler 94'te toplanır — kalanı sabit genişlikli işlemler kolonu (6rem) alır.
 */
export function systemParameterColumns({ t, localized, onEdit, readOnly = false }: ColumnOpts): ReactElement[] {
  const columns = [
    <Column
      key="code"
      field="code"
      header={t('admin.systemParameters.columns.code')}
      style={{ width: '20%' }}
      sortable
    />,
    <Column
      key="name"
      header={t('admin.systemParameters.columns.name')}
      // İki kolon tek kolona indi; genişlik ikisinin toplamı kadar kalır ki diğer kolonların
      // yerleşimi kaymasın.
      style={{ width: '56%' }}
      body={(row: SystemParameter) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="value"
      field="value"
      header={t('admin.systemParameters.columns.value')}
      style={{ width: '18%' }}
    />,
    <Column
      key="actions"
      header={t('admin.systemParameters.columns.actions')}
      style={{ width: '6rem' }}
      body={(row: SystemParameter) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('admin.systemParameters.actions.edit')}
            tooltip={t('admin.systemParameters.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
        </div>
      )}
    />,
  ]
  // Salt okunurda tek eylem (Düzenle) düşer → kolon boş kalacağı için tümüyle çıkarılır.
  return readOnly ? columns.filter((c) => c.key !== 'actions') : columns
}
